"use client";

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type MutableRefObject, type ReactNode } from "react";
import { usePathname, useRouter } from "next/navigation";
import { Button } from "@/components/Button";
import { Dialog } from "@/components/Dialog";

type GuardedAction = () => void | Promise<void>;
type GuardRegistration = {
  dirtyRef: MutableRefObject<boolean>;
  discardRef: MutableRefObject<GuardedAction>;
};

type UnsavedChangesContextValue = {
  register: (dirtyRef: MutableRefObject<boolean>, discardRef: MutableRefObject<GuardedAction>) => () => void;
  protect: (action: GuardedAction) => void;
};

const passthroughContext: UnsavedChangesContextValue = {
  register: () => () => undefined,
  protect: (action) => { void action(); },
};

const UnsavedChangesContext = createContext<UnsavedChangesContextValue>(passthroughContext);

export function UnsavedChangesProvider({ children }: { children: ReactNode }) {
  const pathname = usePathname();
  const router = useRouter();
  const registrationsRef = useRef<Set<GuardRegistration>>(new Set());
  const pendingActionRef = useRef<GuardedAction | null>(null);
  const currentUrlRef = useRef<string | null>(null);
  const currentHistoryStateRef = useRef<unknown>(null);
  const [pendingAction, setPendingAction] = useState<GuardedAction | null>(null);

  const register = useCallback((dirtyRef: MutableRefObject<boolean>, discardRef: MutableRefObject<GuardedAction>) => {
    const registration = { dirtyRef, discardRef };
    registrationsRef.current.add(registration);
    return () => {
      registrationsRef.current.delete(registration);
      if (!registrationsRef.current.size) {
        pendingActionRef.current = null;
        setPendingAction(null);
      }
    };
  }, []);

  const protect = useCallback((action: GuardedAction) => {
    const possuiAlteracoesPendentes = Array.from(registrationsRef.current).some((registration) => registration.dirtyRef.current);
    if (!possuiAlteracoesPendentes) {
      void action();
      return;
    }
    pendingActionRef.current = action;
    setPendingAction(() => action);
  }, []);

  const contextValue = useMemo(() => ({ register, protect }), [protect, register]);

  useEffect(() => {
    currentUrlRef.current = `${window.location.pathname}${window.location.search}${window.location.hash}`;
    currentHistoryStateRef.current = window.history.state;
  }, [pathname]);

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (!Array.from(registrationsRef.current).some((registration) => registration.dirtyRef.current)) return;
      event.preventDefault();
      event.returnValue = "";
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, []);

  useEffect(() => {
    const handleClick = (event: MouseEvent) => {
      if (!Array.from(registrationsRef.current).some((registration) => registration.dirtyRef.current) || event.defaultPrevented || event.button !== 0) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

      const target = event.target;
      if (!(target instanceof Element)) return;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (!anchor || anchor.hasAttribute("download") || (anchor.target && anchor.target !== "_self")) return;

      const href = anchor.getAttribute("href");
      if (!href || href.startsWith("#")) return;

      let url: URL;
      try {
        url = new URL(anchor.href, window.location.href);
      } catch {
        return;
      }
      if ((url.protocol !== "http:" && url.protocol !== "https:") || url.origin !== window.location.origin) return;

      event.preventDefault();
      event.stopPropagation();
      const destino = `${url.pathname}${url.search}${url.hash}`;
      protect(() => {
        currentUrlRef.current = destino;
        currentHistoryStateRef.current = window.history.state;
        router.push(destino);
      });
    };

    document.addEventListener("click", handleClick, true);
    return () => document.removeEventListener("click", handleClick, true);
  }, [protect, router]);

  useEffect(() => {
    const handlePopState = (event: PopStateEvent) => {
      const destino = `${window.location.pathname}${window.location.search}${window.location.hash}`;
      const origem = currentUrlRef.current;
      if (!origem || destino === origem) {
        currentUrlRef.current = destino;
        currentHistoryStateRef.current = event.state;
        return;
      }

      const possuiAlteracoesPendentes = Array.from(registrationsRef.current).some((registration) => registration.dirtyRef.current);
      if (!possuiAlteracoesPendentes) {
        currentUrlRef.current = destino;
        currentHistoryStateRef.current = event.state;
        return;
      }

      event.preventDefault();
      event.stopImmediatePropagation();
      window.history.pushState(currentHistoryStateRef.current, "", origem);
      protect(() => {
        currentUrlRef.current = destino;
        currentHistoryStateRef.current = event.state;
        router.push(destino);
      });
    };

    window.addEventListener("popstate", handlePopState, true);
    return () => window.removeEventListener("popstate", handlePopState, true);
  }, [protect, router]);

  async function descartarAlteracoes() {
    const action = pendingActionRef.current;
    const registrations = Array.from(registrationsRef.current).filter((registration) => registration.dirtyRef.current);
    pendingActionRef.current = null;
    setPendingAction(null);
    for (const registration of registrations) {
      await registration.discardRef.current();
    }
    await action?.();
  }

  function voltarParaEdicao() {
    pendingActionRef.current = null;
    setPendingAction(null);
  }

  return (
    <UnsavedChangesContext.Provider value={contextValue}>
      {children}
      <Dialog
        open={Boolean(pendingAction)}
        onOpenChange={(open) => { if (!open) voltarParaEdicao(); }}
        closeOnEscape={false}
        closeOnBackdrop={false}
        aria-labelledby="titulo-alteracoes-nao-salvas"
        aria-describedby="descricao-alteracoes-nao-salvas"
        className="relative z-10 max-h-[min(90vh,48rem)] w-full max-w-md overflow-auto rounded-2xl border border-[var(--fz-overlay-surface-border)] bg-[var(--fz-overlay-surface-bg)] p-5 text-[var(--fz-overlay-surface-text)] shadow-[var(--fz-overlay-surface-shadow)] outline-none sm:p-6"
      >
        <h2 id="titulo-alteracoes-nao-salvas" className="text-lg font-bold text-navy">Alterações não salvas</h2>
        <p id="descricao-alteracoes-nao-salvas" className="mt-2 text-sm text-muted">Você fez alterações que ainda não foram salvas.</p>
        <div className="mt-5 flex flex-wrap justify-end gap-2">
          <Button type="button" variant="outline" size="md" onClick={voltarParaEdicao} className="h-10">Voltar à edição</Button>
          <Button type="button" variant="danger" size="md" onClick={() => void descartarAlteracoes()} className="h-10">Descartar alterações</Button>
        </div>
      </Dialog>
    </UnsavedChangesContext.Provider>
  );
}

export function useUnsavedChangesGuard({ dirty, onDiscard }: { dirty: boolean; onDiscard: GuardedAction }) {
  const context = useContext(UnsavedChangesContext);
  const dirtyRef = useRef(dirty);
  const discardRef = useRef(onDiscard);
  dirtyRef.current = dirty;
  discardRef.current = onDiscard;

  useEffect(() => context.register(dirtyRef, discardRef), [context]);

  return { protegerSaida: context.protect };
}

export function useUnsavedChangesNavigation() {
  const context = useContext(UnsavedChangesContext);
  return { protegerSaida: context.protect };
}
